import React, { useState, useRef, useEffect } from "react";
import { Eye, FileText, ChevronDown } from "lucide-react";

// Deterministic, pleasant avatar color from a string (name/email).
const AVATAR_COLORS = ["#003E87", "#0F766E", "#B45309", "#7C3AED", "#BE123C", "#15803D", "#0369A1", "#9333EA", "#C2410C"];
const colorFor = (key = "") => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initialsOf = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Avatar = ({ peer, onJump }) => {
  const here = !!peer.here;
  const title = here ? `${peer.name} — on this sheet` : `${peer.name} — viewing ${peer.drawing_name || "another sheet"} · click to open`;
  return (
    <button
      type="button"
      data-testid={`presence-avatar-${peer.user_id}`}
      title={title}
      onClick={() => { if (!here && onJump) onJump(peer); }}
      className={`relative -ml-2 first:ml-0 w-7 h-7 rounded-full ring-2 shadow-sm flex items-center justify-center text-[10px] font-semibold text-white overflow-hidden transition-transform hover:-translate-y-0.5 hover:z-50 ${here ? "ring-emerald-400 cursor-default" : "ring-white opacity-85 hover:opacity-100 cursor-pointer"}`}
      style={{ background: colorFor(peer.email || peer.name || peer.user_id) }}
    >
      {peer.picture ? (
        <img src={peer.picture} alt={peer.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
      ) : (
        initialsOf(peer.name)
      )}
      {here ? (
        <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" />
      ) : (
        <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full bg-white ring-1 ring-slate-200 flex items-center justify-center" data-testid={`presence-jump-${peer.user_id}`}>
          <FileText className="w-2 h-2 text-slate-500" />
        </span>
      )}
    </button>
  );
};

// "Who's here right now" stack + a roster popover listing everyone on the bid and the sheet
// they're viewing. Clicking an off-sheet teammate (avatar or roster row) jumps to their sheet.
export default function PresenceStack({ peers = [], onJump }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  if (!peers.length) return null;
  const shown = peers.slice(0, 5);
  const extra = peers.length - shown.length;
  return (
    <div className="relative flex items-center gap-1.5 pl-2 pr-1" data-testid="presence-stack" ref={ref}>
      <Eye className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
      <div className="flex items-center">
        {shown.map((p) => <Avatar key={p.user_id} peer={p} onJump={onJump} />)}
        {extra > 0 && (
          <div className="relative -ml-2 w-7 h-7 rounded-full ring-2 ring-white bg-slate-200 text-slate-600 text-[10px] font-semibold flex items-center justify-center" data-testid="presence-overflow">
            +{extra}
          </div>
        )}
      </div>
      <button data-testid="presence-roster-toggle" onClick={() => setOpen((o) => !o)} title="Who's on this bid"
        className="ml-0.5 flex items-center gap-0.5 text-[11px] text-slate-500 hover:text-[#003E87] rounded px-1 py-0.5">
        <span className="font-medium">{peers.length}</span><ChevronDown className={`w-3 h-3 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-40 w-64 bg-white rounded-xl border border-slate-200 shadow-xl p-2" data-testid="presence-roster">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 px-1 pb-1">{peers.length} teammate{peers.length === 1 ? "" : "s"} on this bid</div>
          <div className="space-y-0.5 max-h-64 overflow-auto">
            {peers.map((p) => (
              <button key={p.user_id} data-testid={`presence-roster-row-${p.user_id}`}
                onClick={() => { if (!p.here && onJump) onJump(p); setOpen(false); }}
                className={`w-full flex items-center gap-2 rounded-lg px-1.5 py-1.5 text-left ${p.here ? "bg-emerald-50/60 cursor-default" : "hover:bg-slate-50 cursor-pointer"}`}>
                <span className="relative w-7 h-7 rounded-full ring-2 ring-white flex items-center justify-center text-[10px] font-semibold text-white overflow-hidden shrink-0" style={{ background: colorFor(p.email || p.name || p.user_id) }}>
                  {p.picture ? <img src={p.picture} alt={p.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" /> : initialsOf(p.name)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-slate-800 truncate">{p.name}</div>
                  <div className="text-[10px] text-slate-400 truncate">{p.here ? "on this sheet" : `viewing ${p.drawing_name || "another sheet"}`}</div>
                </div>
                {p.here ? <span className="text-[9px] font-semibold text-emerald-600 shrink-0">here</span> : <span className="text-[10px] text-[#003E87] shrink-0">open →</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
